import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  AlertOctagon,
  Wrench,
  Building2,
  Users,
  BarChart3,
  Settings as SettingsIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { isCommercial } from "@/lib/roles";

// Non-conformites et Receptions marchandises ont ete retires : le module
// Litiges devient le point d'entree unique des incidents. Leurs collections
// et leurs routes API restent en place, dormantes, pour rester reversible.
const NAV_ITEMS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testId: "nav-dashboard", commercial: true },
  { to: "/litiges", label: "Litiges", icon: AlertOctagon, testId: "nav-complaints", commercial: true },
  { to: "/actions", label: "Actions correctives", icon: Wrench, testId: "nav-actions", commercial: false },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Building2, testId: "nav-suppliers", commercial: false },
  { to: "/clients", label: "Clients", icon: Users, testId: "nav-clients", commercial: true },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3, testId: "nav-stats", commercial: false },
  { to: "/parametres", label: "Paramètres", icon: SettingsIcon, testId: "nav-settings", commercial: true },
];

export default function Sidebar({ onItemClick }) {
  const _loc = useLocation();
  const { user } = useAuth();
  const { logoUrl, company_name } = useBranding();
  const commercial = isCommercial(user);
  const items = NAV_ITEMS.filter((i) => !commercial || i.commercial);

  return (
    <aside
      className="bg-white border-r border-slate-100 w-64 min-h-screen flex flex-col py-6 flex-shrink-0"
      data-testid="sidebar"
    >
      <div className="px-6 pb-6 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm">
            <img src={logoUrl} alt={company_name} className="h-9 w-9 object-contain" data-testid="sidebar-logo" />
          </div>
          <div>
            <div className="text-base font-bold tracking-tight text-slate-900 font-heading">
              {company_name}
            </div>
            <div className="text-[11px] tracking-[0.12em] uppercase text-slate-400 font-semibold">
              Qualité · QHSE
            </div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onItemClick}
            data-testid={item.testId}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group",
                isActive
                  ? "bg-gradient-to-r from-blue-700 to-slate-900 text-white shadow-sm"
                  : "text-slate-600 hover:bg-blue-50 hover:text-blue-700",
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  size={18}
                  strokeWidth={1.75}
                  className={cn(
                    "transition-transform",
                    isActive ? "text-white" : "text-slate-400 group-hover:text-blue-600",
                  )}
                />
                <span className="truncate">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-6 pt-4 border-t border-slate-100 text-xs text-slate-400">
        <div>ISO 9001 · v1.1</div>
        {commercial ? (
          <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-blue-700 font-bold">
            Compte commercial
          </div>
        ) : null}
      </div>
    </aside>
  );
}
